import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../db/banco'
import type { Compromisso } from '../db/tipos'
import { dataISOLocal } from '../pedidos/precos'
import { useAgora } from '../lib/useAgora'
import { confirmar } from './confirmar'
import { atrasados, diaDaSemanaCurto, horaDeAlertar, textoDoAtraso } from '../compromissos/regras'
import { alternarSituacao, excluirCompromisso } from '../compromissos/gravar'
import { pendenciasDeRetornoDeHoje, excluirAvisoDeRetorno } from '../visitas/logica'
import type { Retorno } from '../visitas/retorno'

// Teste dela 04/10, item 6: compromisso atrasado abre uma janela vermelha ao
// entrar no app e de hora em hora, ate ser feito ou excluido. So com o app
// aberto (avisar com ele fechado e notificacao do celular, outro ciclo).
// O "ultimo" mora na memoria: recarregar o app conta como abrir de novo.
// Dentro de /compromissos nao abre -- a pessoa ja esta resolvendo.
// Visual: print dela de 04/10 (cabecalho vermelho, card por compromisso com
// Feito/Excluir ali mesmo, Abrir Agenda e Lembrar depois). Dourado em OURO
// VELHO, como o cabecalho (pedido dela 04/10).
export default function AlertaAtrasados() {
  const agora = useAgora()
  const local = useLocation()
  const navegar = useNavigate()
  const hoje = dataISOLocal(agora)
  const doPassado = useLiveQuery(
    () => banco.compromissos.where('data').belowOrEqual(hoje).toArray(),
    [hoje],
  )
  const lista = doPassado ? atrasados(doPassado, agora) : []
  // Ciclo 2B: retorno de cliente atrasado tambem entra aqui, ate Feito
  // (registrar a visita na rota) ou Excluir (so cala o aviso).
  const retornosLive = useLiveQuery(() => pendenciasDeRetornoDeHoje(new Date()), [hoje])
  const retornos = (retornosLive ?? []).filter((r) => r.diasDeAtraso > 0)
  const total = lista.length + retornos.length
  const [ultimo, setUltimo] = useState<number | null>(null)
  const [aberto, setAberto] = useState(false)
  const [gravando, setGravando] = useState(false)
  const naAgendaDeCompromissos = local.pathname.startsWith('/compromissos')

  useEffect(() => {
    if (aberto || naAgendaDeCompromissos || total === 0) return
    if (!horaDeAlertar(ultimo, agora.getTime())) return
    setAberto(true)
    setUltimo(agora.getTime())
  }, [aberto, naAgendaDeCompromissos, total, ultimo, agora])

  // Resolveu tudo pela janela: fecha de vez (nao reabre sozinha no proximo atraso
  // antes da hora).
  useEffect(() => {
    if (aberto && doPassado && retornosLive && total === 0) setAberto(false)
  }, [aberto, doPassado, retornosLive, total])

  if (!aberto || total === 0) return null

  const fechar = () => setAberto(false)
  const abrirAgenda = () => { fechar(); navegar(`/compromissos?data=${lista[0]?.data ?? hoje}`) }
  const irParaRota = () => { fechar(); navegar('/agenda') }
  async function excluirRetorno(r: Retorno) {
    if (!(await confirmar('Excluir este aviso? O cliente continua na rota até ser visitado.', { botao: 'Excluir', perigo: true }))) return
    setGravando(true)
    try { await excluirAvisoDeRetorno(r.cliente.id, r.vencimento) } finally { setGravando(false) }
  }
  async function feito(c: Compromisso) {
    setGravando(true)
    try { await alternarSituacao(c) } finally { setGravando(false) }
  }
  async function excluir(c: Compromisso) {
    if (!(await confirmar('Excluir este compromisso?', { botao: 'Excluir', perigo: true }))) return
    setGravando(true)
    try { await excluirCompromisso(c.id) } finally { setGravando(false) }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4">
      <div role="alertdialog" aria-modal="true" aria-label="Compromissos atrasados" data-testid="alerta-atrasados"
        className="w-full max-w-sm max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="relative bg-gradient-to-br from-red-500 via-red-600 to-red-700 text-white px-4 pt-4 pb-6 flex gap-3">
          <div className="pointer-events-none absolute -right-12 -bottom-20 w-44 h-24 rounded-full bg-[linear-gradient(135deg,#f1dc9c,#cfa951_50%,#8a6620)] opacity-90" />
          <div className="relative shrink-0 w-14 h-14 rounded-full bg-red-800 ring-4 ring-[#cfa951]/80 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="5" width="15" height="15" rx="2" /><path d="M7 3v4M14 3v4M3 10h15M7 14h4" />
              <path d="M18 13l4 7h-8z" fill="white" stroke="white" strokeLinejoin="round" /><path d="M18 15.5v2" stroke="#b91c1c" />
            </svg>
          </div>
          <div className="relative min-w-0 flex-1 pr-8">
            <p className="text-xl font-bold leading-tight">
              {retornos.length === 0
                ? (lista.length === 1 ? '1 compromisso' : `${lista.length} compromissos`)
                : (total === 1 ? '1 pendência' : `${total} pendências`)}{' '}
              <span className="texto-ouro-claro sombra-3d">
                {retornos.length === 0
                  ? (lista.length === 1 ? 'atrasado' : 'atrasados')
                  : (total === 1 ? 'atrasada' : 'atrasadas')}
              </span>
            </p>
            <p className="mt-1 text-sm text-white/90">Marque como feito ou exclua para parar este aviso.</p>
          </div>
          <button type="button" onClick={fechar} aria-label="Fechar"
            className="absolute right-3 top-3 w-9 h-9 rounded-full bg-white text-red-600 text-xl font-bold flex items-center justify-center shadow">
            ×
          </button>
        </div>

        <ul className="-mt-3 relative rounded-t-3xl bg-white px-3 pt-4 space-y-3 overflow-y-auto">
          {lista.map((c) => (
            <li key={c.id} data-testid={`atrasado-${c.id}`} className="flex items-center gap-2 rounded-2xl border border-slate-100 bg-slate-50 p-2 shadow-sm">
              <div className="shrink-0 w-14 rounded-xl bg-red-50 py-1.5 text-center">
                <p className="font-bold text-red-700 leading-tight">{c.data.slice(8)}/{c.data.slice(5, 7)}</p>
                <p className="text-xs text-slate-500">{diaDaSemanaCurto(c.data)}</p>
                <p className="mx-1 mt-1 rounded-md bg-red-100 text-xs font-bold text-red-700">{c.hora}</p>
              </div>
              <div className="min-w-0 flex-1 border-l border-slate-200 pl-2">
                <p className="font-bold text-slate-900 leading-tight line-clamp-2 break-words">{c.assunto}</p>
                <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                  </svg>
                  {textoDoAtraso(c, agora)}
                </p>
              </div>
              <button type="button" onClick={() => void feito(c)} disabled={gravando} aria-label={`Marcar como feito: ${c.assunto}`}
                className="shrink-0 w-14 flex flex-col items-center gap-0.5 text-[11px] font-semibold leading-tight text-[#8a6620]">
                <span className="w-10 h-10 rounded-xl bg-[#f6ecd2] flex items-center justify-center text-lg">✓</span>
                Feito
              </button>
              <button type="button" onClick={() => void excluir(c)} disabled={gravando} aria-label={`Excluir: ${c.assunto}`}
                className="shrink-0 w-12 flex flex-col items-center gap-0.5 text-[11px] font-semibold leading-tight text-red-600">
                <span className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" />
                  </svg>
                </span>
                Excluir
              </button>
            </li>
          ))}
          {retornos.map((r) => {
            const nome = r.cliente.nome_fantasia || r.cliente.razao_social
            return (
              <li key={`ret-${r.cliente.id}`} data-testid={`atrasado-retorno-${r.cliente.id}`} className="flex items-center gap-2 rounded-2xl border border-slate-100 bg-slate-50 p-2 shadow-sm">
                <div className="shrink-0 w-14 rounded-xl bg-red-50 py-1.5 text-center">
                  <p className="font-bold text-red-700 leading-tight">{r.vencimento.slice(8)}/{r.vencimento.slice(5, 7)}</p>
                  <p className="mx-1 mt-1 rounded-md bg-red-100 text-[11px] font-bold text-red-700">Retorno</p>
                </div>
                <div className="min-w-0 flex-1 border-l border-slate-200 pl-2">
                  <p className="font-bold text-slate-900 leading-tight line-clamp-2 break-words">Retorno: {nome}</p>
                  <p className="mt-1 text-xs text-red-600">atrasado há {r.diasDeAtraso} dia(s)</p>
                </div>
                <button type="button" onClick={irParaRota} disabled={gravando} aria-label={`Marcar como feito: Retorno ${nome}`}
                  className="shrink-0 w-14 flex flex-col items-center gap-0.5 text-[11px] font-semibold leading-tight text-[#8a6620]">
                  <span className="w-10 h-10 rounded-xl bg-[#f6ecd2] flex items-center justify-center text-lg">✓</span>
                  Feito
                </button>
                <button type="button" onClick={() => void excluirRetorno(r)} disabled={gravando} aria-label={`Excluir: Retorno ${nome}`}
                  className="shrink-0 w-12 flex flex-col items-center gap-0.5 text-[11px] font-semibold leading-tight text-red-600">
                  <span className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" />
                    </svg>
                  </span>
                  Excluir
                </button>
              </li>
            )
          })}
        </ul>

        <div className="px-3 pt-4 pb-4 space-y-2 bg-white">
          <button type="button" autoFocus onClick={abrirAgenda}
            className="w-full rounded-2xl bg-gradient-to-r from-red-700 to-red-500 text-white py-3 font-bold shadow-md flex items-center justify-center gap-2 animate-pulse">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" />
            </svg>
            Abrir Agenda ›
          </button>
          <button type="button" onClick={fechar}
            className="w-full rounded-2xl border-2 border-[#cfa951] bg-[#fbf6e9] py-2.5 font-semibold text-[#7a5a1c] flex items-center justify-center gap-2">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10 21h4M3 3l18 18" />
            </svg>
            Lembrar depois
          </button>
        </div>
      </div>
    </div>
  )
}
